import { useState, useEffect } from 'react';
import {
  IconAlertCircle,
  IconChevronDown,
  IconCircleCheck,
  IconCloudUpload,
  IconInfoCircle,
  IconLoader2,
  IconTrash,
} from '@tabler/icons-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import PageHeader from '@/components/PageHeader';
import MainCard from '@/components/MainCard';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface SimType {
  id: number;
  name: string;
}

interface Distributor {
  id: number;
  i_customer: number;
  company_name: string;
  code: string;
}

interface Activation {
  id: number;
  name: string;
  distributor_i_customer: number;
}

export function SimImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tempCount, setTempCount] = useState(0);

  // Commit form fields
  const [simTypes, setSimTypes] = useState<SimType[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [activations, setActivations] = useState<Activation[]>([]);
  const [selectedSimType, setSelectedSimType] = useState<SimType | null>(null);
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);
  const [selectedActivation, setSelectedActivation] = useState<Activation | null>(null);
  const [nextOrderId, setNextOrderId] = useState<string>('');

  const [simTypeOpen, setSimTypeOpen] = useState(false);
  const [distributorOpen, setDistributorOpen] = useState(false);
  const [activationOpen, setActivationOpen] = useState(false);

  // Load data on page load
  useEffect(() => {
    loadTempCount();
    loadSimTypes();
    loadDistributors();
    loadNextOrderId();
  }, []);

  // Load activations when distributor changes
  useEffect(() => {
    if (selectedDistributor) {
      loadActivations(selectedDistributor.i_customer);
    } else {
      setActivations([]);
      setSelectedActivation(null);
    }
  }, [selectedDistributor]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError('');
      setSuccess('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/sim-cards/sim-import/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccess(res.data.message || `Uploaded ${res.data.count} SIM cards`);
      setFile(null);
      loadTempCount();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const loadTempCount = async () => {
    try {
      const res = await api.get('/sim-cards/sim-import/temp-count');
      setTempCount(res.data.count || 0);
    } catch (err) {
      console.error('Failed to load temp count', err);
    }
  };

  const loadSimTypes = async () => {
    try {
      const res = await api.get('/sim-cards/sim-import/sim-types');
      const sortedTypes = (res.data || []).sort((a: SimType, b: SimType) =>
        a.name.localeCompare(b.name)
      );
      setSimTypes(sortedTypes);
    } catch (err) {
      console.error('Failed to load SIM types', err);
    }
  };

  const loadDistributors = async () => {
    try {
      const res = await api.get('/sim-cards/sim-import/distributors');
      setDistributors(res.data || []);
    } catch (err) {
      console.error('Failed to load distributors', err);
    }
  };

  const loadActivations = async (distributorICustomer: number) => {
    try {
      const res = await api.get(`/sim-cards/sim-import/activations?distributorICustomer=${distributorICustomer}`);
      setActivations(res.data || []);
    } catch (err) {
      console.error('Failed to load activations', err);
    }
  };

  const loadNextOrderId = async () => {
    try {
      const res = await api.get('/sim-cards/sim-import/next-order-id');
      setNextOrderId(res.data.orderId || '');
    } catch (err) {
      console.error('Failed to load next order ID', err);
    }
  };

  const handleClearTemp = async () => {
    if (!confirm('Clear all SIM cards from temp table?')) return;

    try {
      await api.post('/sim-cards/sim-import/clear-temp');
      setSuccess('Temp table cleared');
      setTempCount(0);
      loadNextOrderId(); // Reload order ID
    } catch (err: any) {
      setError('Failed to clear temp table');
    }
  };

  // Helper function to check if SIM type is "Unassigned eSIM" (handles typos/variations)
  const isUnassignedEsimType = (simType: SimType | null): boolean => {
    if (!simType) return false;
    const name = simType.name.toLowerCase().trim();
    // Check for variations: "unassigned", "unasssigned" (with 3 s's), "un-assigned", etc. and "esim"
    // Using 'unassign' so it matches both "unassigned" and "unasssigned" (any string containing "unassign")
    const hasUnassigned = name.includes('unassign');
    const hasEsim = name.includes('esim');
    return hasUnassigned && hasEsim;
  };

  const handleCommit = async () => {
    if (!selectedSimType) {
      setError('Please select a SIM type');
      return;
    }

    // Check if "Unassigned eSIM" is selected - skip distributor/activation requirements
    const isUnassignedEsim = isUnassignedEsimType(selectedSimType);

    if (!isUnassignedEsim) {
      if (!selectedDistributor) {
        setError('Please select a distributor');
        return;
      }
      if (!selectedActivation) {
        setError('Please select an activation');
        return;
      }
    }

    const numberOfOuterBoxes = Math.ceil(tempCount / 5000);
    if (!confirm(`Commit ${tempCount} SIM cards to main table?\n\nOrder ID: ${nextOrderId}\nOuter Boxes: ${numberOfOuterBoxes}`)) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const commitData: any = {
        simTypeId: selectedSimType.id,
      };

      // Only include distributor and activation if not "Unassigned eSIM"
      if (!isUnassignedEsim && selectedDistributor && selectedActivation) {
        commitData.distributorICustomer = selectedDistributor.i_customer;
        commitData.activationId = selectedActivation.id;
      }

      const res = await api.post('/sim-cards/sim-import/commit', commitData);
      setSuccess(res.data.message || 'SIM cards committed successfully');
      setTempCount(0);
      setSelectedSimType(null);
      setSelectedDistributor(null);
      setSelectedActivation(null);
      loadNextOrderId(); // Load next order ID
    } catch (err: any) {
      setError(err.response?.data?.message || 'Commit failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="SIM Card Import"
        subtitle="Upload CSV file to import SIM cards in bulk"
      />

      {/* CSV Format Description */}
      <MainCard className="mb-3 bg-muted/50">
        <h2 className="mb-2 text-lg font-semibold tracking-tight">CSV Format Requirements</h2>
        <p className="mb-6 text-sm text-muted-foreground">
          Your CSV file must contain the following columns:
        </p>

        <div className="mb-6">
          <h3 className="mb-2 text-sm font-semibold text-foreground">Required Fields:</h3>
          <div className="pl-2 text-sm text-muted-foreground">
            • <strong className="text-foreground">imsi</strong> - International Mobile Subscriber Identity
            <br />
            • <strong className="text-foreground">iccid</strong> - Integrated Circuit Card Identifier
            <br />
            • <strong className="text-foreground">puk_1</strong> - Personal Unlocking Key (first)
            <br />• <strong className="text-foreground">puk_2</strong> - Personal Unlocking Key (second)
          </div>
        </div>

        <div className="mb-6">
          <h3 className="mb-2 text-sm font-semibold text-foreground">Optional Fields:</h3>
          <div className="pl-2 text-sm text-muted-foreground">
            • <strong className="text-foreground">qr_code</strong> - QR code for the SIM card
          </div>
        </div>

        <p className="text-sm italic text-muted-foreground">
          Note: The CSV file should have a header row with column names. All required fields must be present in the
          CSV.
        </p>
      </MainCard>

      {/* Upload Section */}
      <MainCard className="mb-3">
        <div className="flex flex-col gap-6">
          <div>
            <input
              id="sim-import-csv-input"
              type="file"
              className="sr-only"
              accept=".csv"
              onChange={handleFileChange}
            />
            <label
              htmlFor="sim-import-csv-input"
              className={cn(
                buttonVariants({ variant: 'outline', size: 'default' }),
                'flex w-full cursor-pointer items-center justify-center gap-2 py-8'
              )}
            >
              <IconCloudUpload className="size-4 shrink-0" />
              <span className="truncate">{file ? file.name : 'Choose CSV File'}</span>
            </label>
          </div>

          {file && (
            <Alert variant="info">
              <IconInfoCircle className="size-4" />
              <AlertTitle>File selected</AlertTitle>
              <AlertDescription>
                {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <IconAlertCircle className="size-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert variant="success">
              <IconCircleCheck className="size-4" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <Button
            variant="default"
            onClick={handleUpload}
            disabled={!file || loading}
            size="lg"
            className="w-full gap-2"
          >
            {loading ? (
              <IconLoader2 className="size-5 animate-spin" />
            ) : (
              <IconCloudUpload className="size-5 shrink-0" />
            )}
            {loading ? 'Uploading...' : 'Upload CSV'}
          </Button>
        </div>
      </MainCard>

      {/* Temp Table Status */}
      <Card className="mb-3">
        <CardContent className="pt-6">
          <h2 className="mb-2 text-lg font-semibold tracking-tight">Temp Table Status</h2>
          <p className="mb-2 text-4xl font-semibold text-primary">{tempCount.toLocaleString()}</p>
          <p className="mb-4 text-sm text-muted-foreground">SIM cards in temp table</p>

          {tempCount > 0 && (
            <Alert variant="info" className="mb-4">
              <IconInfoCircle className="size-4" />
              <AlertTitle>Order details</AlertTitle>
              <AlertDescription>
                Next Order ID: <strong>{nextOrderId}</strong> | Outer Boxes:{' '}
                <strong>{Math.ceil(tempCount / 5000)}</strong> ({(tempCount / 5000).toFixed(2)} boxes)
              </AlertDescription>
            </Alert>
          )}

          <Button
            variant="outline"
            onClick={handleClearTemp}
            disabled={tempCount === 0}
            className="w-full gap-2"
          >
            <IconTrash className="size-4 shrink-0" />
            Clear Temp Table
          </Button>
        </CardContent>
      </Card>

      {/* Commit Configuration */}
      {tempCount > 0 &&
        (() => {
          const isUnassignedEsim = isUnassignedEsimType(selectedSimType);

          const isButtonDisabled =
            !selectedSimType || loading || (!isUnassignedEsim && (!selectedDistributor || !selectedActivation));

          return (
            <Card>
              <CardContent className="pt-6">
                <h2 className="mb-2 text-lg font-semibold tracking-tight">Commit Configuration</h2>
                <p className="mb-6 text-sm text-muted-foreground">
                  Configure SIM card details before committing to main table
                </p>

                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium leading-none">SIM Type</span>
                    <Popover open={simTypeOpen} onOpenChange={setSimTypeOpen}>
                      <PopoverTrigger
                        type="button"
                        role="combobox"
                        aria-expanded={simTypeOpen}
                        className={cn(
                          buttonVariants({ variant: 'outline', size: 'default' }),
                          'h-auto min-h-8 w-full justify-between gap-2 py-2 font-normal'
                        )}
                      >
                        <span className="truncate text-left">
                          {selectedSimType ? selectedSimType.name : 'Search SIM types...'}
                        </span>
                        <IconChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                      </PopoverTrigger>
                      <PopoverContent className="w-[min(100vw-2rem,28rem)] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search SIM types..." />
                          <CommandList>
                            <CommandEmpty>No SIM type found.</CommandEmpty>
                            <CommandGroup>
                              {simTypes.map((option) => (
                                <CommandItem
                                  key={option.id}
                                  value={`${option.id}-${option.name}`}
                                  onSelect={() => {
                                    setSelectedSimType(option);
                                    setSimTypeOpen(false);
                                  }}
                                >
                                  {option.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {!isUnassignedEsim && (
                    <>
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium leading-none">Distributor</span>
                        <Popover open={distributorOpen} onOpenChange={setDistributorOpen}>
                          <PopoverTrigger
                            type="button"
                            role="combobox"
                            aria-expanded={distributorOpen}
                            className={cn(
                              buttonVariants({ variant: 'outline', size: 'default' }),
                              'h-auto min-h-8 w-full justify-between gap-2 py-2 font-normal'
                            )}
                          >
                            <span className="truncate text-left">
                              {selectedDistributor
                                ? selectedDistributor.company_name
                                : 'Search distributors...'}
                            </span>
                            <IconChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                          </PopoverTrigger>
                          <PopoverContent className="w-[min(100vw-2rem,28rem)] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search distributors..." />
                              <CommandList>
                                <CommandEmpty>No distributor found.</CommandEmpty>
                                <CommandGroup>
                                  {distributors.map((option) => (
                                    <CommandItem
                                      key={option.id}
                                      value={`${option.id}-${option.company_name}`}
                                      onSelect={() => {
                                        setSelectedDistributor(option);
                                        setDistributorOpen(false);
                                      }}
                                    >
                                      {option.company_name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium leading-none">Activation / Starter Pack</span>
                        <Popover
                          open={activationOpen}
                          onOpenChange={(open) => {
                            if (open && !selectedDistributor) return;
                            setActivationOpen(open);
                          }}
                        >
                          <PopoverTrigger
                            type="button"
                            role="combobox"
                            aria-expanded={activationOpen}
                            disabled={!selectedDistributor}
                            className={cn(
                              buttonVariants({ variant: 'outline', size: 'default' }),
                              'h-auto min-h-8 w-full justify-between gap-2 py-2 font-normal'
                            )}
                          >
                            <span className="truncate text-left">
                              {selectedActivation
                                ? selectedActivation.name || `Activation ${selectedActivation.id}`
                                : 'Search activations...'}
                            </span>
                            <IconChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                          </PopoverTrigger>
                          <PopoverContent className="w-[min(100vw-2rem,28rem)] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search activations..." />
                              <CommandList>
                                <CommandEmpty>No activation found.</CommandEmpty>
                                <CommandGroup>
                                  {activations.map((option) => (
                                    <CommandItem
                                      key={option.id}
                                      value={`${option.id}-${option.name || `activation-${option.id}`}`}
                                      onSelect={() => {
                                        setSelectedActivation(option);
                                        setActivationOpen(false);
                                      }}
                                    >
                                      {option.name || `Activation ${option.id}`}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </>
                  )}

                  <Separator />

                  <Button
                    variant="default"
                    size="lg"
                    disabled={isButtonDisabled}
                    onClick={handleCommit}
                    className="w-full gap-2"
                  >
                    {loading ? (
                      <IconLoader2 className="size-5 animate-spin" />
                    ) : (
                      <IconCircleCheck className="size-5 shrink-0" />
                    )}
                    {loading
                      ? 'Committing...'
                      : `Commit ${tempCount.toLocaleString()} SIM Cards to Main Table`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}
    </div>
  );
}
